// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { useFlag } from '@calendly/feature-check-react'
import { I18n } from '@calendly/i18n'
import { IconButton } from '@calendly/ui/components/icon-button'
import { CloseIcon, QuestionCircleIcon } from '@calendly/ui/components/icons'
import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import LoadingState from 'components/core/loading_state'
import { Container } from 'components/organizations/layout'
import {
  LearnMoreButton,
  OccupancyRatio,
  PageHeaderWithTabs,
} from 'components/page_elements'
import { getOverviewHelpCenterLink } from 'features/contextual_help'
import { CONTENT_PAGES, useNewEmptyStates } from 'features/empty_state'
import { fetchStatistics, statisticsSelector } from 'features/organization_statistics'
import { UpgradeBanner } from 'features/upgrade_banner'
import {
  ActiveUsers,
  AddNewUser,
  MigratingUsers,
  PendingUsers,
  totalInvitationCountSelector,
  totalMigrationCountSelector,
  totalUserCountSelector,
} from 'features/users_page'
import { toSentenceCase } from 'shared/utils/to_sentence_case'

import { mergeSettingsSearch, parseSettingsTabFromSearch, SETTINGS_SECTION } from './constants'
import { guardSettingsModalTabNavigation } from './guard_settings_modal_tabs'
import { useIsSettingsModalLayout } from './layout_context'

const addCounterToString = (title, counter) => {
  if (typeof counter !== 'number') {
    return title
  }
  return `${title} (${counter})`
}

const useSettingsModalOrgDirectoryTabs = () => {
  const location = useLocation()
  const usersCount = useSelector(totalUserCountSelector)
  const invitationsCount = useSelector(totalInvitationCountSelector)
  const migrationsCount = useSelector(totalMigrationCountSelector)
  const hasAppConsolidationFeature = useFlag('calendly_app_consolidation')

  const rawTab = parseSettingsTabFromSearch(location.search)

  const allowedSlugs = useMemo(() => {
    const base = ['active_users', 'pending_users']
    const migrating = hasAppConsolidationFeature ? ['migrating_users'] : []
    return [...base, ...migrating]
  }, [hasAppConsolidationFeature])

  const effectiveTab = allowedSlugs.includes(rawTab ?? '') ? rawTab : allowedSlugs[0]

  const tabs = useMemo(() => {
    const rows = [
      {
        slug: 'active_users',
        title: addCounterToString(
          I18n.t('publisher.organization.members.member_status.active'),
          usersCount,
        ),
      },
      {
        slug: 'pending_users',
        title: addCounterToString(
          I18n.t('publisher.organization.members.member_status.pending'),
          invitationsCount,
        ),
      },
    ]
    if (hasAppConsolidationFeature) {
      rows.push({
        slug: 'migrating_users',
        title: addCounterToString(
          I18n.t('publisher.organization.members.member_status.migrating'),
          migrationsCount,
        ),
      })
    }
    return rows
      .filter((r) => allowedSlugs.includes(r.slug))
      .map((r) => ({
        key: r.slug,
        title: r.title,
        route: {
          pathname: location.pathname,
          search: mergeSettingsSearch(SETTINGS_SECTION.orgDirectory, location.search, r.slug),
          hash: location.hash,
        },
        selected: effectiveTab === r.slug,
      }))
  }, [
    allowedSlugs,
    effectiveTab,
    hasAppConsolidationFeature,
    invitationsCount,
    location.hash,
    location.pathname,
    location.search,
    migrationsCount,
    usersCount,
  ])

  return {
    effectiveTab,
    location,
    rawTab,
    tabs,
  }
}

export const SettingsModalOrgDirectoryHeader = ({
  onClose,
  showCloseInHeader = true,
}) => {
  const isNewEmptyStatesEnabled = useNewEmptyStates()
  const { occupancyRatio } = useSelector(statisticsSelector)
  const { effectiveTab, tabs } = useSettingsModalOrgDirectoryTabs()
  const usersHelpCenterUrl = useMemo(
    () => getOverviewHelpCenterLink(CONTENT_PAGES.users),
    [],
  )

  const closeAction = showCloseInHeader ? (
    <IconButton
      key="settings-modal-close"
      type="button"
      title={I18n.t('publisher.action.close')}
      aria-label={I18n.t('publisher.action.close')}
      onClick={onClose}
    >
      <CloseIcon />
    </IconButton>
  ) : null

  const activeUsersToolbarActions = effectiveTab === 'active_users' ? [
    ...(isNewEmptyStatesEnabled && usersHelpCenterUrl ? [
      <LearnMoreButton
        key="org-directory-help-learn-more"
        as="a"
        href={usersHelpCenterUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={I18n.t('publisher.contextual_help.page_title.adding_users_overview')}
      >
        <QuestionCircleIcon />
      </LearnMoreButton>,
    ] : []),
    <AddNewUser key="settings-modal-add-new-user" />,
  ] : []

  return (
    <PageHeaderWithTabs
      wide
      title={toSentenceCase(I18n.t('publisher.personal_settings.navigation.org_directory'))}
      description={effectiveTab === 'active_users' ? (
        <OccupancyRatio ratio={occupancyRatio} isAdminIa />
      ) : undefined}
      tabs={guardSettingsModalTabNavigation(tabs)}
      hasBottomMargin={false}
      actions={[
        ...activeUsersToolbarActions,
        ...(closeAction ? [closeAction] : []),
      ]}
    />
  )
}

export const SettingsModalOrgDirectoryPage = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const inSettingsModal = useIsSettingsModalLayout()
  const isNewEmptyStatesEnabled = useNewEmptyStates()
  const {
    effectiveTab,
    location,
    rawTab,
  } = useSettingsModalOrgDirectoryTabs()

  useEffect(() => {
    if (rawTab === effectiveTab || rawTab == null) {
      return
    }
    history.replace({
      pathname: location.pathname,
      search: mergeSettingsSearch(SETTINGS_SECTION.orgDirectory, location.search, effectiveTab),
      hash: location.hash,
    })
  }, [effectiveTab, history, location.hash, location.pathname, location.search, rawTab])

  useEffect(() => {
    dispatch(fetchStatistics())
  }, [dispatch, location.pathname])

  if (!inSettingsModal) {
    return <LoadingState />
  }

  return (
    <>
      {effectiveTab === 'active_users' && (
        <Container>
          {isNewEmptyStatesEnabled && (
            <UpgradeBanner featureKey="organization_invitation" contentKey={CONTENT_PAGES.users} />
          )}
          <ActiveUsers />
        </Container>
      )}
      {effectiveTab === 'pending_users' && (
        <Container>
          <PendingUsers />
        </Container>
      )}
      {effectiveTab === 'migrating_users' && (
        <Container>
          <MigratingUsers />
        </Container>
      )}
    </>
  )
}
